import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Chip,
  Stack,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import DescriptionIcon from '@mui/icons-material/Description';
import { makeApiCall } from '../../config/api';

const QuoteReport = () => {
  const { t } = useTranslation();
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [status, setStatus] = useState('all');
  const [customers, setCustomers] = useState([]);
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [salesPersons, setSalesPersons] = useState([]);
  const [selectedSalesPersons, setSelectedSalesPersons] = useState([]);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [generatingPDF, setGeneratingPDF] = useState(false);

  // Fetch Sales Persons
  useEffect(() => {
    const fetchSalesPersons = async () => {
      try {
        const response = await makeApiCall('api/customers/lookups/sales-persons/search?limit=100');
        if (response.ok) {
          const result = await response.json();
          setSalesPersons(result.sales_persons || []);
        }
      } catch (err) {
        console.error('Error fetching sales persons:', err);
      }
    };
    fetchSalesPersons();

    // Auto-load last 30 days
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - 30);
    setFromDate(from);
    setToDate(to);
  }, []);

  // Customer search (debounced)
  const searchCustomers = async (query) => {
    try {
      if (!query) return;
      const response = await makeApiCall(`api/customers?search=${query}&limit=20`);
      if (response.ok) {
        const result = await response.json();
        setCustomers(result.customers || []);
      }
    } catch (err) {
      console.error('Error searching customers:', err);
    }
  };

  const fetchReport = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (fromDate) params.append('from_date', fromDate.toISOString());
      if (toDate) params.append('to_date', toDate.toISOString());
      
      if (status && status !== 'all') {
        params.append('status', status);
      }

      if (selectedCustomers.length > 0) {
        const ids = selectedCustomers.map(c => c.id).join(',');
        params.append('customer_ids', ids);
      }

      if (selectedSalesPersons.length > 0) {
        const ids = selectedSalesPersons.map(sp => sp.id).join(',');
        params.append('sales_person_ids', ids);
      }

      const response = await makeApiCall(`api/reports/quotes?${params}`);
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setData(result.data);
        } else {
          setError(t('reports.error.fetching'));
        }
      } else {
        setError(t('reports.error.fetching'));
      }
    } catch (err) {
      console.error('Error fetching quotes report:', err);
      setError(t('reports.error.fetching'));
    } finally {
      setLoading(false);
    }
  };

  const downloadPDF = async () => {
    setGeneratingPDF(true);
    setError(null);
    try {
      const customerIds = selectedCustomers.length > 0 
        ? selectedCustomers.map(c => c.id).join(',') 
        : null;
        
      const salesPersonIds = selectedSalesPersons.length > 0 
        ? selectedSalesPersons.map(sp => sp.id).join(',') 
        : null;

      const response = await makeApiCall('api/reports/quotes/pdf', {
        method: 'POST',
        body: JSON.stringify({
          from_date: fromDate ? fromDate.toISOString() : null,
          to_date: toDate ? toDate.toISOString() : null,
          status: status === 'all' ? null : status,
          customer_ids: customerIds,
          sales_person_ids: salesPersonIds
        })
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `quotes-report-${Date.now()}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        setError(t('reports.error.pdf'));
      }
    } catch (err) {
      console.error('Error generating PDF:', err);
      setError(t('reports.error.pdf'));
    } finally {
      setGeneratingPDF(false);
    }
  };

  useEffect(() => {
    if (fromDate && toDate) {
      fetchReport();
    }
  }, []); 

  const formatDateForInput = (date) => {
    if (!date) return '';
    const d = new Date(date);
    return d.toISOString().split('T')[0];
  };

  const handleFromDateChange = (e) => {
    setFromDate(e.target.value ? new Date(e.target.value) : null);
  };

  const handleToDateChange = (e) => {
    setToDate(e.target.value ? new Date(e.target.value) : null);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'success';
      case 'rejected': return 'error';
      case 'pending': return 'warning';
      case 'draft': return 'default';
      case 'expired': return 'error';
      default: return 'default';
    }
  };

  return (
    <Box>
      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
          {t('reports.filters')}
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 3 }}>
            <TextField
              fullWidth
              label={t('reports.fromDate')}
              type="date"
              value={formatDateForInput(fromDate)}
              onChange={handleFromDateChange}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <TextField
              fullWidth
              label={t('reports.toDate')}
              type="date"
              value={formatDateForInput(toDate)}
              onChange={handleToDateChange}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <FormControl fullWidth>
              <InputLabel>{t('reports.status')}</InputLabel>
              <Select
                value={status}
                label={t('reports.status')}
                onChange={(e) => setStatus(e.target.value)}
              >
                <MenuItem value="all">{t('common.all')}</MenuItem>
                <MenuItem value="draft">{t('quotes.draft')}</MenuItem>
                <MenuItem value="pending">{t('quotes.pending')}</MenuItem>
                <MenuItem value="approved">{t('quotes.approved')}</MenuItem>
                <MenuItem value="rejected">{t('quotes.rejected')}</MenuItem>
                <MenuItem value="expired">{t('quotes.expired')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <Autocomplete
              multiple
              options={salesPersons}
              getOptionLabel={(option) => option.display_name || `${option.first_name} ${option.last_name}`}
              value={selectedSalesPersons}
              onChange={(event, newValue) => setSelectedSalesPersons(newValue)}
              renderInput={(params) => (
                <TextField 
                  {...params} 
                  label={t('reports.quotesReport.salesPerson')} 
                  placeholder={t('reports.selectEmployee')}
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Autocomplete
              multiple
              options={customers}
              getOptionLabel={(option) => option.company_name || `${option.first_name} ${option.last_name}`}
              value={selectedCustomers}
              onChange={(event, newValue) => setSelectedCustomers(newValue)}
              onInputChange={(event, newInputValue) => {
                if (newInputValue.length > 2) {
                  searchCustomers(newInputValue);
                }
              }}
              renderInput={(params) => (
                <TextField 
                  {...params} 
                  label={t('reports.customer')} 
                  placeholder={t('crm.searchCustomers')}
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button
                variant="contained"
                startIcon={<RefreshIcon />}
                onClick={fetchReport}
                disabled={loading || !fromDate || !toDate}
                sx={{ bgcolor: '#1976d2' }}
              >
                {t('reports.load')}
              </Button>
              <Button
                variant="contained"
                startIcon={generatingPDF ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
                onClick={downloadPDF}
                disabled={generatingPDF || data.length === 0}
                sx={{ bgcolor: '#d32f2f' }}
              >
                PDF
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Summary */}
      {data.length > 0 && (
        <Card sx={{ mb: 3, bgcolor: '#fff3e0' }}>
          <CardContent>
            <Stack direction="row" spacing={3} alignItems="center">
              <DescriptionIcon color="warning" />
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {t('reports.quotesReport.title')} Summary
              </Typography>
              <Chip label={`Total: ${data.length}`} color="default" />
              <Chip 
                label={`${t('reports.quotesReport.totalValue')}: ${formatCurrency(data.reduce((sum, q) => sum + parseFloat(q.total_amount || 0), 0))}`} 
                color="success" 
              />
              <Chip 
                label={`${t('reports.quotesReport.approved')}: ${data.filter(q => q.status === 'approved').length}`} 
                color="primary" 
                variant="outlined"
              />
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Report Data */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : data.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            {t('reports.noData')}
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell><strong>{t('reports.date')}</strong></TableCell>
                <TableCell><strong>{t('quotes.quoteNumber')}</strong></TableCell>
                <TableCell><strong>{t('reports.customer')}</strong></TableCell>
                <TableCell><strong>Reference</strong></TableCell>
                <TableCell><strong>{t('reports.status')}</strong></TableCell>
                <TableCell><strong>{t('reports.quotesReport.salesPerson')}</strong></TableCell>
                <TableCell align="right"><strong>{t('quotes.totalAmount')}</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>{new Date(row.created_at).toLocaleDateString()}</TableCell>
                  <TableCell>{row.quote_number}</TableCell>
                  <TableCell>
                    {row.company_name || `${row.customer_first_name} ${row.customer_last_name}`}
                  </TableCell>
                  <TableCell>{row.quote_reference || '-'}</TableCell>
                  <TableCell>
                    <Chip 
                      label={row.status} 
                      size="small" 
                      color={getStatusColor(row.status)}
                    />
                  </TableCell>
                  <TableCell>{row.sales_person_name || row.created_by_username || '-'}</TableCell>
                  <TableCell align="right">{formatCurrency(row.total_amount)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default QuoteReport;

